import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  PlusCircle,
  Edit,
  Trash2,
  X,
  Phone,
  Mail,
  CreditCard,
  RefreshCw,
  Camera,
  ImagePlus,
} from "lucide-react";
import RFIDGravingModal from "./RFIDGravingModal";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { apiFetch } from "../utils/apiClient";
import StewardManagement from "./StewardManagement";

const API_BASE = "/api";

const roleMetadata = {
  admin: { label: "Administrateur", color: "text-rose-300 bg-rose-500/10" },
  manager: { label: "Manager", color: "text-sky-300 bg-sky-500/10" },
  cashier: { label: "Caissier", color: "text-emerald-300 bg-emerald-500/10" },
  server: { label: "Serveur", color: "text-amber-300 bg-amber-500/10" },
  stockeur: { label: "Stockeur", color: "text-teal-300 bg-teal-500/10" },
  kitchen: { label: "Cuisine", color: "text-orange-300 bg-orange-500/10" },
  bar: { label: "Bar", color: "text-purple-300 bg-purple-500/10" },
};

const getAvatarFallback = (user) => {
  const seed =
    user?.username ||
    `${user?.first_name || ""}-${user?.last_name || ""}` ||
    "user";
  return `https://avatar.vercel.sh/${encodeURIComponent(seed)}.svg?text=${encodeURIComponent(
    `${(user?.first_name || "M")[0] || ""}${(user?.last_name || "S")[0] || ""}`,
  )}`;
};

const getUserDisplayName = (user) => {
  const first = String(user?.first_name || "").trim();
  const last = String(user?.last_name || "").trim();
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;
  const username = String(user?.username || "").trim();
  if (username) return username;
  return "Utilisateur";
};

const isZoneScopedRole = (role) =>
  ["cashier", "caissier", "caisse", "server", "serveur"].includes(
    String(role || "").trim().toLowerCase(),
  );

const UserModal = ({
  user,
  onSave,
  onCancel,
  isDarkMode,
  roleOptions,
  floorPlanEnabled,
  zones,
}) => {
  const [formData, setFormData] = useState(user);
  const [errors, setErrors] = useState({});
  const [isGeneratingPin, setIsGeneratingPin] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || "");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFormData(user);
    setErrors({});
    setAvatarPreview(user?.avatar_url || "");
  }, [user]);

  const generateUniquePin = async () => {
    setIsGeneratingPin(true);
    try {
      const response = await fetch(`${API_BASE}/generate-unique-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ excludeUserId: formData.id }),
      });

      if (response.ok) {
        const data = await response.json();
        setFormData((prev) => ({ ...prev, pin_code: data.pin_code }));
        if (errors.pin_code) {
          setErrors((prev) => ({ ...prev, pin_code: "" }));
        }
      }
    } catch (error) {
      console.error("Erreur génération PIN:", error);
    } finally {
      setIsGeneratingPin(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const toggleZone = (zoneId) => {
    setFormData((previous) => {
      const current = Array.isArray(previous.zone_ids)
        ? previous.zone_ids.map(Number)
        : [];
      const normalizedId = Number(zoneId);
      return {
        ...previous,
        zone_ids: current.includes(normalizedId)
          ? current.filter((id) => id !== normalizedId)
          : [...current, normalizedId],
      };
    });
    if (errors.zone_ids) {
      setErrors((previous) => ({ ...previous, zone_ids: "" }));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      setAvatarPreview(result);
      setFormData((prev) => ({ ...prev, avatar_url: result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatarPreview("");
    setFormData((prev) => ({ ...prev, avatar_url: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username?.trim())
      newErrors.username = "Nom d'utilisateur requis";
    if (!formData.first_name?.trim()) newErrors.first_name = "Prénom requis";
    if (!formData.role) newErrors.role = "Rôle requis";
    if (
      floorPlanEnabled &&
      isZoneScopedRole(formData.role) &&
      (!Array.isArray(formData.zone_ids) || formData.zone_ids.length === 0)
    ) {
      newErrors.zone_ids = "Sélectionnez au moins une aire";
    }
    if (!formData.id && !formData.password?.trim())
      newErrors.password = "Mot de passe requis";
    if (formData.password && formData.password.length < 6)
      newErrors.password = "Mot de passe trop court (min 6 caractères)";
    if (!formData.pin_code?.trim()) {
      newErrors.pin_code = "Code PIN requis";
    } else if (!/^\d{6}$/.test(formData.pin_code)) {
      newErrors.pin_code = "Le code PIN doit contenir exactement 6 chiffres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({ ...formData, avatar_url: avatarPreview || "" });
    }
  };

  if (!user) return null;

  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const modalBg = isDarkMode
    ? "bg-[#0f172a] border border-white/10 text-slate-100"
    : "bg-white border border-slate-200 text-slate-900";
  const inputClasses = isDarkMode
    ? "bg-white/5 border-white/15 text-slate-100 placeholder-white/40"
    : "bg-white border-slate-200 text-slate-900";
  const selectClasses = isDarkMode
    ? "appearance-none bg-slate-900/80 text-slate-100 border-white/15"
    : "appearance-none bg-white text-slate-900 border-slate-200";

  const roles = roleOptions;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur">
      <div
        className={`${modalBg} w-full max-w-2xl rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto`}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
                {formData.id ? "Modifier" : "Nouvel utilisateur"}
              </p>
              <h2 className="text-2xl font-semibold">
                {formData.first_name || "Nouveau"}{" "}
                {formData.last_name || "profil"}
              </h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex flex-col items-center gap-3 md:w-1/3">
              <div className="relative">
                <img
                  src={avatarPreview || getAvatarFallback(formData)}
                  alt="Avatar"
                  className="h-28 w-28 rounded-3xl object-cover border border-white/10 shadow-lg"
                  onError={(e) => {
                    e.currentTarget.src = getAvatarFallback(formData);
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-2 right-2 rounded-full bg-amber-400 text-slate-900 p-2 shadow-lg hover:bg-amber-300"
                >
                  <Camera size={16} />
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs px-3 py-1 rounded-full border border-white/10 hover:border-amber-400/60 flex items-center gap-1"
                >
                  <ImagePlus size={14} />
                  Importer
                </button>
                {avatarPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    className="text-xs px-3 py-1 rounded-full border border-white/10 hover:border-rose-400/60"
                  >
                    Retirer
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <p className={`text-xs text-center ${textMuted}`}>
                Photo optionnelle pour personnaliser le compte.
              </p>
            </div>

            <div className="space-y-4 md:flex-1">
              <div>
                <label className={`text-sm font-semibold ${textMuted}`}>
                  Nom d'utilisateur *
                </label>
                <input
                  name="username"
                  value={formData.username || ""}
                  onChange={handleChange}
                  placeholder="nom_utilisateur"
                  className={`mt-1 w-full rounded-2xl px-3 py-2 border ${inputClasses} ${
                    errors.username ? "border-rose-500" : ""
                  }`}
                />
                {errors.username && (
                  <p className="text-xs text-rose-400 mt-1">
                    {errors.username}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm font-semibold ${textMuted}`}>
                    Prénom *
                  </label>
                  <input
                    name="first_name"
                    value={formData.first_name || ""}
                    onChange={handleChange}
                    className={`mt-1 w-full rounded-2xl px-3 py-2 border ${inputClasses} ${
                      errors.first_name ? "border-rose-500" : ""
                    }`}
                  />
                  {errors.first_name && (
                    <p className="text-xs text-rose-400 mt-1">
                      {errors.first_name}
                    </p>
                  )}
                </div>
                <div>
                  <label className={`text-sm font-semibold ${textMuted}`}>
                    Nom (optionnel)
                  </label>
                  <input
                    name="last_name"
                    value={formData.last_name || ""}
                    onChange={handleChange}
                    className={`mt-1 w-full rounded-2xl px-3 py-2 border ${inputClasses}`}
                  />
                </div>
              </div>

              <div>
                <label className={`text-sm font-semibold ${textMuted}`}>
                  Rôle *
                </label>
                <select
                  name="role"
                  value={formData.role || ""}
                  onChange={handleChange}
                  className={`mt-1 w-full rounded-2xl px-3 py-2 border ${selectClasses} ${
                    errors.role ? "border-rose-500" : ""
                  }`}
                >
                  <option value="">Sélectionner un rôle</option>
                  {roles
                    .filter((role) => role.enabled !== false)
                    .map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="text-xs text-rose-400 mt-1">{errors.role}</p>
                )}
              </div>

              {floorPlanEnabled &&
                isZoneScopedRole(formData.role) && (
                  <div>
                    <label className={`text-sm font-semibold ${textMuted}`}>
                      Aires autorisées *
                    </label>
                    <div
                      className={`mt-2 grid grid-cols-1 gap-2 rounded-2xl border p-3 ${
                        errors.zone_ids
                          ? "border-rose-500"
                          : isDarkMode
                            ? "border-white/10 bg-white/5"
                            : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      {(zones || []).map((zone) => {
                        const checked = (formData.zone_ids || [])
                          .map(Number)
                          .includes(Number(zone.id));
                        return (
                          <label
                            key={zone.id}
                            className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-white/5"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleZone(zone.id)}
                              className="accent-amber-500"
                            />
                            <span className="text-sm">{zone.name}</span>
                          </label>
                        );
                      })}
                      {zones.length === 0 && (
                        <p className="text-sm text-amber-400">
                          Aucune aire active n'est disponible.
                        </p>
                      )}
                    </div>
                    {errors.zone_ids && (
                      <p className="mt-1 text-xs text-rose-400">
                        {errors.zone_ids}
                      </p>
                    )}
                  </div>
                )}

              <div>
                <label className={`text-sm font-semibold ${textMuted}`}>
                  Mot de passe {formData.id ? "(optionnel)" : "*"}
                </label>
                <input
                  name="password"
                  type="password"
                  value={formData.password || ""}
                  onChange={handleChange}
                  placeholder={
                    formData.id ? "Laisser vide pour conserver" : "••••••"
                  }
                  className={`mt-1 w-full rounded-2xl px-3 py-2 border ${inputClasses} ${
                    errors.password ? "border-rose-500" : ""
                  }`}
                />
                {errors.password && (
                  <p className="text-xs text-rose-400 mt-1">
                    {errors.password}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Téléphone
              </label>
              <input
                name="phone"
                value={formData.phone || ""}
                onChange={handleChange}
                placeholder="+225 XX XX XX XX"
                className={`mt-1 w-full rounded-2xl px-3 py-2 border ${inputClasses}`}
              />
            </div>
            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Email
              </label>
              <input
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleChange}
                placeholder="email@exemple.com"
                className={`mt-1 w-full rounded-2xl px-3 py-2 border ${inputClasses}`}
              />
            </div>
          </div>

          <div>
            <label className={`text-sm font-semibold ${textMuted}`}>
              Code PIN (6 chiffres) *
            </label>
            <div className="flex gap-2 mt-1">
              <input
                name="pin_code"
                type="text"
                value={formData.pin_code || ""}
                onChange={handleChange}
                readOnly
                className={`flex-1 rounded-2xl px-3 py-2 border font-mono text-center text-lg tracking-[0.4em] ${inputClasses} ${
                  errors.pin_code ? "border-rose-500" : ""
                }`}
              />
              <button
                type="button"
                onClick={generateUniquePin}
                disabled={isGeneratingPin}
                className="rounded-2xl border border-white/15 px-3 py-2 text-sm font-semibold hover:border-amber-400/60 disabled:opacity-60 flex items-center gap-2"
              >
                <RefreshCw
                  size={16}
                  className={isGeneratingPin ? "animate-spin" : ""}
                />
                Générer
              </button>
            </div>
            {errors.pin_code && (
              <p className="text-xs text-rose-400 mt-1">{errors.pin_code}</p>
            )}
            <p className={`text-xs mt-1 ${textMuted}`}>
              Code utilisé pour les sessions rapides et validations critiques.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              name="is_active"
              checked={Boolean(formData.is_active)}
              onChange={handleChange}
              className="rounded accent-amber-500"
            />
            <span className={textMuted}>Utilisateur actif</span>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-2xl border border-white/15 px-4 py-2 text-sm font-semibold hover:border-amber-400/60"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-amber-500/30"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const location = useLocation();
  const isSuperPath = location.pathname.startsWith("/superadmin");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [quotaError, setQuotaError] = useState("");
  const [rfidModalOpen, setRfidModalOpen] = useState(false);
  const [selectedUserForRfid, setSelectedUserForRfid] = useState(null);
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const [floorPlanEnabled, setFloorPlanEnabled] = useState(false);
  const [zones, setZones] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const isBoutique = businessType === "boutique";

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () =>
      setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;

    const loadBusinessType = async () => {
      try {
        const response = await fetch(`${API_BASE}/restaurant-info`);
        if (!response.ok) throw new Error("restaurant-info");
        const data = await response.json();
        if (!active) return;
        setBusinessType(extractBusinessType(data));
        try {
          localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
        } catch (_e) {}
      } catch (_error) {
        try {
          const cached = localStorage.getItem("restaurant-info");
          if (!cached) return;
          const parsed = JSON.parse(cached);
          if (active) setBusinessType(extractBusinessType(parsed));
        } catch (_e) {}
      }
    };

    loadBusinessType();
    return () => {
      active = false;
    };
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];
      const ts = Date.now();
      const fetchOptions = {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      };

      if (isSuperPath) {
        const resp = await fetch(`${API_BASE}/users?_ts=${ts}`, fetchOptions);
        if (!resp.ok) throw new Error("Erreur réseau ou serveur");
        data = await resp.json();
      } else {
        const response = await fetch(`${API_BASE}/users?_ts=${ts}`, fetchOptions);
        if (!response.ok) throw new Error("Erreur réseau ou serveur");
        data = await response.json();
        data = data.filter((u) => u.role !== "superadmin");
      }

      setUsers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isSuperPath]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const loadFloorPlan = useCallback(async () => {
      try {
        const requestOptions = isSuperPath
          ? {
              includeBearer: true,
              tokenMode: "superadmin",
              credentials: "include",
              cache: "no-store",
            }
          : { cache: "no-store" };
        const settingsResponse = await apiFetch(
          `${API_BASE}/settings/floor-plan?_ts=${Date.now()}`,
          requestOptions,
        );
        if (!settingsResponse.ok) throw new Error("floor-plan settings");
        const settingsPayload = await settingsResponse.json();
        const enabled = Boolean(settingsPayload?.floor_plan_enabled);
        setFloorPlanEnabled(enabled);
        if (!enabled) {
          setZones([]);
          return;
        }
        const zonesResponse = await apiFetch(
          `${API_BASE}/zones?_ts=${Date.now()}`,
          requestOptions,
        );
        if (!zonesResponse.ok) throw new Error("floor-plan zones");
        const zonesPayload = await zonesResponse.json();
        setZones(Array.isArray(zonesPayload) ? zonesPayload : []);
      } catch (_error) {
        setFloorPlanEnabled(false);
        setZones([]);
      }
  }, [isSuperPath]);

  useEffect(() => {
    void loadFloorPlan();
    const handleUpdate = () => void loadFloorPlan();
    const handleStorage = (event) => {
      if (
        event.key === "moo_floor_plan_enabled" ||
        event.key === "moo_floor_plan_broadcast"
      ) {
        void loadFloorPlan();
      }
    };
    window.addEventListener("moo-floor-plan-updated", handleUpdate);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleUpdate);
    return () => {
      window.removeEventListener("moo-floor-plan-updated", handleUpdate);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleUpdate);
    };
  }, [loadFloorPlan]);

  // Synchroniser les packages pour appliquer rôles/quotas même entre navigateurs
  useEffect(() => {
    const syncPackages = async () => {
      try {
        const res = await fetch(`/api/packages?_ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
          cache: "no-store",
        });
        if (res.ok && res.status !== 304) {
          const data = await res.json();
          const serverPkgs = Array.isArray(data.packages) ? data.packages : [];
          const activeId = data.activeId || serverPkgs[0]?.id || "";
          localStorage.setItem("moo_packages", JSON.stringify(serverPkgs));
          localStorage.setItem("moo_active_package", activeId);
          localStorage.setItem("moo_package_broadcast", `${activeId}_${Date.now()}`);
        }
      } catch (e) {
        console.warn("Sync packages (users) échoué", e);
      }
    };
    syncPackages();
  }, [isSuperPath]);

  const checkQuotaForRole = (targetRole, excludeUserId = null) => {
    try {
      const rawPackages = localStorage.getItem("moo_packages");
      const activeId = localStorage.getItem("moo_active_package");
      if (rawPackages && activeId) {
        const list = JSON.parse(rawPackages) || [];
        const active = list.find((p) => p.id === activeId);
        const limit = active?.roleLimits?.[targetRole];
        if (limit !== undefined && limit !== null && limit !== "") {
          const parsedLimit = parseInt(limit, 10);
          if (!Number.isNaN(parsedLimit)) {
            const currentCount = users.filter(
              (u) => u.role === targetRole && (!excludeUserId || u.id !== excludeUserId),
            ).length;
            if (currentCount >= parsedLimit) {
              setEditingUser(null);
              setQuotaError(
                `Limite atteinte pour le rôle ${
                  roleMetadata[targetRole]?.label || targetRole
                }. Quota autorisé : ${parsedLimit}.`,
              );
              return false;
            }
          }
        }
      }
    } catch (e) {
      console.error("Erreur lecture package quotas:", e);
    }
    return true;
  };

  const handleSaveUser = async (user) => {
    // Vérifier quotas package pour création ou changement de rôle
    if (!user.id) {
      if (!checkQuotaForRole(user.role)) return;
    } else {
      const existing = users.find((u) => u.id === user.id);
      const previousRole = existing?.role;
      if (previousRole && previousRole !== user.role) {
        if (!checkQuotaForRole(user.role, user.id)) return;
      }
    }

    const url = user.id ? `${API_BASE}/users/${user.id}` : `${API_BASE}/users`;
    const method = user.id ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(user),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Échec de la sauvegarde");
      }
      await fetchUsers();
      setEditingUser(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")
    ) {
      try {
        const response = await fetch(`${API_BASE}/users/${userId}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || "Échec de la suppression");
        }
        await fetchUsers();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleAddNew = async () => {
    try {
      const response = await fetch(`${API_BASE}/generate-unique-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      let generatedPin = "123456";
      if (response.ok) {
        const data = await response.json();
        generatedPin = data.pin_code;
      }

      setEditingUser({
        username: "",
        password: "",
        pin_code: generatedPin,
        first_name: "",
        last_name: "",
        role: "",
        phone: "",
        email: "",
        avatar_url: "",
        is_active: true,
        zone_ids: [],
      });
    } catch (error) {
      console.error("Erreur génération PIN:", error);
      setEditingUser({
        username: "",
        password: "",
        pin_code: "123456",
        first_name: "",
        last_name: "",
        role: "",
        phone: "",
        email: "",
        avatar_url: "",
        is_active: true,
        zone_ids: [],
      });
    }
  };

  const handleRfidGraving = (user) => {
    setSelectedUserForRfid(user);
    setRfidModalOpen(true);
  };

  const handleRfidSuccess = async () => {
    await fetchUsers();
  };

  const buildRoleOptions = () => {
    try {
      const rawPackages = localStorage.getItem("moo_packages");
      const activeId = localStorage.getItem("moo_active_package");
      if (rawPackages && activeId) {
        const list = JSON.parse(rawPackages) || [];
        const active = list.find((p) => p.id === activeId);
        const allowedRoles = active?.allowedRoles;
        const baseRoles = [
          { value: "admin", label: "Administrateur" },
          { value: "manager", label: "Manager" },
          { value: "cashier", label: "Caissier" },
          { value: "server", label: "Serveur" },
          { value: "stockeur", label: "Stockeur" },
          { value: "kitchen", label: "Cuisine" },
          { value: "bar", label: "Bar" },
        ];
        if (allowedRoles && Object.keys(allowedRoles).length > 0) {
          const filtered = baseRoles.filter(
            (r) =>
              ["kitchen", "bar"].includes(r.value) ||
              allowedRoles[r.value] === true,
          );
          if (filtered.length > 0) {
            return filtered;
          }
        }
        return baseRoles;
      }
    } catch (e) {
      console.error("Erreur lecture allowedRoles package:", e);
    }
    return [
      { value: "admin", label: "Administrateur" },
      { value: "manager", label: "Manager" },
      { value: "cashier", label: "Caissier" },
      { value: "server", label: "Serveur" },
      { value: "stockeur", label: "Stockeur" },
      { value: "kitchen", label: "Cuisine" },
      { value: "bar", label: "Bar" },
    ];
  };

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0c182f] to-[#050b18] text-slate-100"
    : "bg-slate-50 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/60";

  const stats = [
    { label: "Membres", value: users.length || 0 },
    { label: "Actifs", value: users.filter((u) => u.is_active).length },
    { label: "Admins", value: users.filter((u) => u.role === "admin").length },
    ...(isBoutique
      ? [
          {
            label: "Cartes RFID",
            value: users.filter((u) => u.rfid_card_uid).length,
          },
        ]
      : []),
  ];

  const sortedUsers = [...users].sort((a, b) =>
    getUserDisplayName(a).localeCompare(getUserDisplayName(b)),
  );
  const roleOptions = buildRoleOptions();

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      {quotaError && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 px-4">
          <div
            className={`w-full max-w-md rounded-3xl p-6 shadow-2xl ${
              isDarkMode
                ? "bg-[#0f172a] border border-white/10 text-slate-100"
                : "bg-white border border-slate-200 text-slate-900"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-400">
                  Quota atteint
                </p>
                <h3 className="text-lg font-semibold">Action bloquée</h3>
              </div>
              <button
                onClick={() => setQuotaError("")}
                className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
              >
                <X size={18} />
              </button>
            </div>
            <p className={isDarkMode ? "text-slate-200" : "text-slate-700"}>{quotaError}</p>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setQuotaError("")}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}
      {editingUser && (
        <UserModal
          user={editingUser}
          onSave={handleSaveUser}
          onCancel={() => setEditingUser(null)}
          isDarkMode={isDarkMode}
          roleOptions={roleOptions}
          floorPlanEnabled={floorPlanEnabled}
          zones={zones}
        />
      )}

      {isBoutique && (
        <RFIDGravingModal
          isOpen={rfidModalOpen}
          onClose={() => {
            setRfidModalOpen(false);
            setSelectedUserForRfid(null);
          }}
          userData={selectedUserForRfid}
          onSuccess={handleRfidSuccess}
          isDarkMode={isDarkMode}
        />
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        <header className={`${cardClasses} rounded-3xl p-6 space-y-4`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
                Administration
              </p>
              <h1 className="text-3xl font-semibold tracking-tight">
                Équipe MOO Sync
              </h1>
              <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                Centralisez les accès et gardez votre
                staff en phase avec la caisse.
              </p>
            </div>
            <button
              onClick={handleAddNew}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-amber-500/30"
            >
              <PlusCircle size={18} />
              Ajouter un membre
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className={`${cardClasses} rounded-3xl p-4 text-left`}
              >
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                  {stat.label}
                </p>
                <p className="text-2xl font-semibold mt-2">{stat.value}</p>
              </div>
            ))}
          </div>
        </header>

        <StewardManagement
          floorPlanEnabled={floorPlanEnabled}
          isDarkMode={isDarkMode}
        />

        <div className={`${cardClasses} rounded-3xl p-6`}>
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">
              Chargement des utilisateurs...
            </div>
          ) : error ? (
            <div className="py-12 text-center text-rose-400">{error}</div>
          ) : sortedUsers.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              Aucun utilisateur enregistré pour le moment.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sortedUsers.map((user) => {
                const roleInfo = roleMetadata[user.role] || {
                  label: user.role,
                  color: "text-slate-300 bg-white/10",
                };
                const avatarSrc = user.avatar_url || getAvatarFallback(user);
                return (
                  <div
                    key={user.id}
                    className={`rounded-3xl border border-white/10 p-5 flex flex-col gap-4 ${
                      isDarkMode ? "bg-white/5" : "bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <img
                          src={avatarSrc}
                          alt={getUserDisplayName(user)}
                          className="h-14 w-14 rounded-2xl object-cover border border-white/10"
                          onError={(e) => {
                            e.currentTarget.src = getAvatarFallback(user);
                          }}
                        />
                        <span
                          className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 ${
                            user.is_active
                              ? "bg-emerald-400 border-emerald-100"
                              : "bg-rose-400 border-rose-100"
                          }`}
                        ></span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">
                            {getUserDisplayName(user)}
                          </h3>
                        </div>
                        <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          @{user.username}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-[11px] font-semibold rounded-full ${roleInfo.color}`}
                      >
                        {roleInfo.label}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2 text-sm">
                      {user.phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} />
                          {user.phone}
                        </div>
                      )}
                      {user.email && (
                        <div className="flex items-center gap-2">
                          <Mail size={14} />
                          {user.email}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs">
                        <span
                          className={`px-2 py-1 rounded-full ${
                            user.is_active
                              ? "bg-emerald-500/10 text-emerald-400"
                              : "bg-rose-500/10 text-rose-300"
                          }`}
                        >
                          {user.is_active ? "Actif" : "Inactif"}
                        </span>
                        {isBoutique && user.rfid_card_uid && (
                          <span className="px-2 py-1 rounded-full bg-purple-500/10 text-purple-300 text-xs">
                            RFID prête
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                      <small className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>
                        Membre depuis{" "}
                        {new Date(user.created_at).toLocaleDateString("fr-FR")}
                      </small>
                      <div className="flex items-center gap-2">
                        {isBoutique && (
                          <button
                            onClick={() => handleRfidGraving(user)}
                            className="p-2 rounded-full border border-white/10 hover:border-purple-400/60 text-purple-300"
                            title="Graver une carte RFID"
                          >
                            <CreditCard size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-2 rounded-full border border-white/10 hover:border-amber-400/60 text-amber-300"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 rounded-full border border-white/10 hover:border-rose-400/60 text-rose-300"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
